import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
import shared from '../../../src/shared.js';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const Screenshot = makeShortcode("Screenshot");
const Prompt = makeShortcode("Prompt");
const PromptReply = makeShortcode("PromptReply");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`You can enable two-factor authentication (2FA) on your npm user account to protect against unauthorized access to your account and packages, either by using a `}<a parentName="p" {...{
        "href": "https://webauthn.guide/"
      }}>{`security-key`}</a>{` or `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Time-based_one-time_password"
      }}>{`time-based one-time password (TOTP)`}</a>{` from a mobile app.`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note`}</strong>{`: The security-key flow using `}<a parentName="p" {...{
          "href": "https://webauthn.guide/"
        }}>{`WebAuthn`}</a>{` is currently in beta.`}</p>
    </Note>
    <h2>{`Prerequisites`}</h2>
    <p>{`Before you enable 2FA on your npm user account, you must:`}</p>
    <ul>
      <li parentName="ul">{`Update your npm client to version 5.5.1 or higher.`}</li>
      <li parentName="ul">{`To configure a security-key requires a modern browser that support `}<a parentName="li" {...{
          "href": "https://caniuse.com/#search=webauthn"
        }}>{`WebAuthn`}</a>{`. This will allow you to configure a biometric devices such as Apple `}<a parentName="li" {...{
          "href": "https://support.apple.com/en-gb/HT204587"
        }}>{`Touch ID`}</a>{`, `}<a parentName="li" {...{
          "href": "https://support.apple.com/en-us/HT208108"
        }}>{`Face ID`}</a>{`, or `}<a parentName="li" {...{
          "href": "https://support.microsoft.com/en-us/windows/learn-about-windows-hello-and-set-it-up-dae28983-8242-bb2a-d3d1-87c9d265a5f0"
        }}>{`Windows Hello`}</a>{` as well as physical keys such as `}<a parentName="li" {...{
          "href": "https://www.yubico.com/"
        }}>{`Yubikey`}</a>{`, `}<a parentName="li" {...{
          "href": "https://thetis.io/"
        }}>{`Thetis`}</a>{`, or `}<a parentName="li" {...{
          "href": "https://www.ftsafe.com/"
        }}>{`Feitian`}</a>{`.`}</li>
      <li parentName="ul">{`To configure TOTP you will need to install an authenticator application that can generate OTPs such as `}<a parentName="li" {...{
          "href": "https://authy.com/download/"
        }}>{`Authy`}</a>{`, `}<a parentName="li" {...{
          "href": "https://support.google.com/accounts/answer/1066447"
        }}>{`Google Authenticator`}</a>{`, or `}<a parentName="li" {...{
          "href": "https://www.microsoft.com/security/mobile-authenticator-app"
        }}>{`Microsoft Authenticator`}</a>{` on your mobile device.`}</li>
    </ul>
    <p>{`For more information on supported 2FA methods, see "`}<a parentName="p" {...{
        "href": "/about-two-factor-authentication"
      }}>{`About two-factor authentication`}</a>{`".`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` npm does not accept SMS (text-to-phone) as a 2FA method.`}</p>
    </Note>
    <h2>{`Configuring 2FA from the website`}</h2>
    <h3>{`Enabling 2FA`}</h3>
    <ol>
      <li parentName="ol">
        <>{shared['user-login'].text}</>
        <>{shared['user-login'].image}</>
      </li>
      <li parentName="ol">
        <>{shared['account-settings'].text}</>
        <>{shared['account-settings'].image}</>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the account settings page, under "Two-Factor Authentication", click `}<strong parentName="p">{`Enable 2FA`}</strong>{`.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-enable.png" alt="Screenshot showing Enable 2FA button" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`When prompted provide your current account password and then click `}<strong parentName="p">{`Confirm password to continue`}</strong>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the 2FA method page, select the method you would like to enable and click `}<strong parentName="p">{`Continue`}</strong>{`. For more information on supported 2FA methods, see "`}<a parentName="p" {...{
            "href": "/about-two-factor-authentication"
          }}>{`About two-factor authentication`}</a>{`".`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/device-selection.png" alt="Screenshot showing 2FA types" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`Configure the 2FA method of your choice:`}</p>
      </li>
    </ol>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`When using a `}<strong parentName="p">{`security-key`}</strong>{`, provide a name for it and click `}<strong parentName="p">{`Add security key`}</strong>{`. Follow the browser specific steps to add your security-key.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-add-security-key.png" alt="Screenshot showing security key setup" mdxType="Screenshot" />
        <p parentName="li">{`Below is an example of configuration from Microsoft Edge running on a MacOS`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/touch-id-mac-edge.png" alt="Screenshot showing 2FA device selection" mdxType="Screenshot" />
      </li>
      <li parentName="ul">
        <p parentName="li">{`When using an `}<strong parentName="p">{`authenticator application`}</strong>{` on your phone, open it and scan the QR code on the two-step verification page. Enter the code generated by the app, then click `}<strong parentName="p">{`Verify`}</strong>{`.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-verify.png" alt="Screenshot showing 2FA device selection" mdxType="Screenshot" />
      </li>
    </ul>
    <ol {...{
      "start": 7
    }}>
      <li parentName="ol">
        <p parentName="li">{`On the recovery code page, copy the recovery codes to your computer or other safe location that is not your second factor device. We recommend using a password manager.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/recovery-code.png" alt="Screenshot showing the Recovery Code page" mdxType="Screenshot" />
      </li>
    </ol>
    <p>{`   `}<em parentName="p">{`Recovery codes are the only way to recover your account if you lose access to your second factor device. Each code can be used only once. You can `}<a parentName="em" {...{
          "href": "/recovering-your-2fa-enabled-account#viewing-and-regenerating-recovery-code"
        }}>{`view and regenerate your recovery code`}</a>{` from your 2FA settings page.`}</em></p>
    <ol {...{
      "start": 8
    }}>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Go back to settings`}</strong>{` after confirming that you have saved your codes.`}</li>
    </ol>
    <h3>{`Disabling 2FA for writes`}</h3>
    <p>{`Check the `}<a parentName="p" {...{
        "href": "/about-two-factor-authentication#authorization-and-writes"
      }}>{`Authorization and writes`}</a>{` section for more information on different operations that requires 2FA when this mode is enabled.`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note`}</strong>{`: As a recommended setting, 2FA for write operations are `}<em parentName="p">{`automatically enabled`}</em>{` when setting up 2FA. The following steps explain how to disable it. `}</p>
    </Note>
    <ol>
      <li parentName="ol">
        <>{shared['user-login'].text}</>
        <>{shared['user-login'].image}</>
      </li>
      <li parentName="ol">
        <>{shared['account-settings'].text}</>
        <>{shared['account-settings'].image}</>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the account settings page, under "Two-Factor Authentication", click `}<strong parentName="p">{`Modify 2FA`}</strong>{`.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-modify.png" alt="Screenshot showing Modify 2FA button" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`From the "Manage Two-Factor Authentication" navigate to "Additional Options" section`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Clear the checkbox for "Require two-factor authentication for write actions" and click "Update Preferences"`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/disable-2fa-button.png" alt="Screenshot showing a cleared check box to disable 2fa under Addition options" mdxType="Screenshot" />
      </li>
    </ol>
    <h3>{`Disabling 2FA`}</h3>
    <p>{`If you have 2FA enabled, you can remove it from your account settings page.`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` You cannot remove 2FA if you are a member of an organization that enforces 2FA. You can view the list of organizations memberships from your profile page under the "Organizations" tab.`}</p>
    </Note>
    <ol>
      <li parentName="ol">
        <>{shared['user-login'].text}</>
        <>{shared['user-login'].image}</>
      </li>
      <li parentName="ol">
        <>{shared['account-settings'].text}</>
        <>{shared['account-settings'].image}</>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the account settings page, under "Two-Factor Authentication", click `}<strong parentName="p">{`Modify 2FA`}</strong>{`.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-modify.png" alt="Screenshot showing Modify 2FA button" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`Scroll to the bottom of the "Manage Two-Factor Authentication" page and click Disable 2FA.`}</p>
        <Screenshot src="/getting-started/setting-up-your-npm-user-account/2fa-disable.png" alt="Screenshot showing Disable 2FA button" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`Agree to the prompt from the browser.`}</p>
      </li>
    </ol>
    <h2>{`Configuring 2FA from the command line`}</h2>
    <h3>{`Enabling 2FA from the command line`}</h3>
    <p>{`Although security-key with WebAuthn can be used for authentication from both the web and the command line, it can only be configured from the web. When enabling 2FA from the command line, currently the only available option is to use an TOTP mobile app.`}</p>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` Settings you configure on the command line will also apply to your profile settings on the npm website.`}</p>
    </Note>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`If you are logged out on the command line, log in using `}<inlineCode parentName="p">{`npm login`}</inlineCode>{` command.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the command line, type the `}<a parentName="p" {...{
            "href": "/cli/profile"
          }}><inlineCode parentName="a">{`npm profile`}</inlineCode></a>{` command along with the option for the 2FA mode you want to enable:`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`To enable 2FA for authorization and writes, type:`}<br /></p>
            <pre parentName="li"><code parentName="pre" {...{}}>{`npm profile enable-2fa auth-and-writes

`}</code></pre>
          </li>
          <li parentName="ul">
            <p parentName="li">{`To enable 2FA for authorization only, type:`}<br /></p>
            <pre parentName="li"><code parentName="pre" {...{}}>{`npm profile enable-2fa auth-only
`}</code></pre>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`To add npm to your authenticator application, using the device with the app, you can either:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Scan the QR code displayed on the command line.`}</li>
          <li parentName="ul">{`Type the number displayed below the QR code.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When prompted to add an OTP code from your authenticator, on the command line, enter a one-time password generated by your authenticator app.`}</p>
      </li>
    </ol>
    <h3>{`Sending a one-time password from the command line`}</h3>
    <p>{`If you have enabled 2FA auth-and-writes, you will need to send the TOTP from the command line for certain commands to work. To do this, append  `}<inlineCode parentName="p">{`--otp=123456`}</inlineCode>{` (where `}<em parentName="p">{`123456`}</em>{` is the code generated by your authenticator) at the end of the command. Here are a few examples:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm publish [<tarball>|<folder>][--tag <tag>] --otp=123456
npm owner add <user > --otp=123456
npm owner rm <user> --otp=123456
npm dist-tags add <pkg>@<version> [<tag>] --otp=123456
npm access edit [<package>) --otp=123456
npm unpublish [<@scope>/]<pkg>[@<version>] --otp=123456
`}</code></pre>
    <h3>{`Removing 2FA from the command line`}</h3>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`If you are logged out on the command line, log in using `}<inlineCode parentName="p">{`npm login`}</inlineCode>{` command.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the command line, type the following command:`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npm profile disable-2fa
`}</code></pre>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When prompted, enter your npm password:`}</p>
        <Prompt mdxType="Prompt">npm password:</Prompt>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When prompted for a one-time password, enter a password from your authenticator app:`}</p>
        <Prompt mdxType="Prompt">Enter one-time password from your authenticator: <PromptReply mdxType="PromptReply">123456</PromptReply></Prompt>
      </li>
    </ol>
    <h2>{`Resolving TOTP errors`}</h2>
    <p>{`If you are entering what seems to be a valid `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Time-based_one-time_password"
      }}>{`TOTP`}</a>{` but you see an error, be sure that you are using the correct authenticator account. If you have multiple authenticator accounts, using an TOTP from the wrong account will cause an error.`}</p>
    <p>{`Also, when you reset two-factor authentication after it has been disabled, the authenticator might create a second account with the same name. Please see the authenticator documentation to delete the old account.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      